import { Injectable, signal, inject } from '@angular/core';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';

export interface PolicyPermissions {
  show: boolean;
  edit: boolean;
  showMissing: boolean;
  editMissing: boolean;
  showDuplicates: boolean;
  editDuplicates: boolean;
}

export interface ExternalIds {
  salesforceAccountId?: string | null;
  docusignEnvelopeId?: string | null;
  stripeCustomerId?: string | null;
  quickbooksId?: string | null;
}

export interface AgencySettings {
  agencyId: string;
  policyPermissions?: PolicyPermissions;
  features?: {
    enablePolicyManagement?: boolean;
    enableDialer?: boolean;
    enableCommissions?: boolean;
  };
  externalIds?: ExternalIds;
  updatedAt?: number;
  updatedBy?: string;
}

/**
 * Agency Settings Service
 * Manages agency-level settings and permissions
 * Reusable across all products
 */
@Injectable({
  providedIn: 'root',
})
export class AgencySettingsService {
  private apiService = inject(EnrollhereApiService);

  // Cached settings
  settings = signal<AgencySettings | null>(null);
  loading = signal(false);

  /**
   * Load agency settings
   */
  async loadSettings(agencyId: string): Promise<AgencySettings | null> {
    // Return cached if for same agency
    if (this.settings()?.agencyId === agencyId) {
      return this.settings();
    }

    this.loading.set(true);

    return this.apiService
      .get<{ settings: AgencySettings }>(`/agency/${agencyId}/settings`)
      .then(response => {
        this.settings.set(response.settings);
        this.loading.set(false);
        return response.settings;
      })
      .catch(error => {
        console.error('Error loading agency settings', error);
        this.loading.set(false);
        return {
          agencyId,
          policyPermissions: {
            show: false,
            edit: false,
            showMissing: false,
            editMissing: false,
            showDuplicates: false,
            editDuplicates: false,
          },
        };
      });
  }

  /**
   * Update agency settings
   */
  async updateSettings(
    agencyId: string,
    updates: {
      policyPermissions?: Partial<PolicyPermissions>;
      features?: any;
      externalIds?: Partial<ExternalIds>;
    }
  ): Promise<AgencySettings | null> {
    return this.apiService
      .patch<{ success: boolean; settings: AgencySettings }>(
        `/agency/${agencyId}/settings`,
        updates
      )
      .then(response => {
        this.settings.set(response.settings);
        return response.settings;
      })
      .catch(error => {
        console.error('Error updating agency settings', error);
        return null;
      });
  }

  /**
   * Check if policy feature is enabled
   */
  canShowPolicies(): boolean {
    return this.settings()?.policyPermissions?.show ?? false;
  }

  /**
   * Check if can edit policies
   */
  canEditPolicies(): boolean {
    return this.settings()?.policyPermissions?.edit ?? false;
  }

  /**
   * Check if can view missing policies
   */
  canShowMissing(): boolean {
    return this.settings()?.policyPermissions?.showMissing ?? false;
  }

  /**
   * Check if can edit missing policies
   */
  canEditMissing(): boolean {
    return this.settings()?.policyPermissions?.editMissing ?? false;
  }

  /**
   * Check if can view duplicates
   */
  canShowDuplicates(): boolean {
    return this.settings()?.policyPermissions?.showDuplicates ?? false;
  }

  /**
   * Check if can edit duplicates
   */
  canEditDuplicates(): boolean {
    return this.settings()?.policyPermissions?.editDuplicates ?? false;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.settings.set(null);
  }
}
